<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KanbanAPI from '../../../../api/kanban';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  agentInfo: {
    type: Object,
    default: null,
  },
  loadingAgent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['agentAssigned', 'agentRemoved']);

const { t } = useI18n();

const store = useStore();

// Computed para obter todos os agentes atribuídos
const assignedAgents = computed(() => {
  return props.item?.assigned_agents || [];
});

// Computed para obter a lista de agentes disponíveis
const agentList = computed(() => store.getters['agents/getAgents']);

// Modal de atribuição rápida de agente
const showAgentAssignModal = ref(false);
const agentAssignLoading = ref(false);
const selectedAgentId = ref(null);
const agentSearch = ref('');

// Computed para filtrar agentes baseado na busca
const filteredAgentList = computed(() => {
  if (!agentSearch.value) return agentList.value;
  return agentList.value.filter(agent =>
    (agent.name || '').toLowerCase().includes(agentSearch.value.toLowerCase())
  );
});

// Computed para obter o agente da conversa (se existir)
const conversationAgent = computed(() => {
  const conversation = props.item?.conversation;
  return conversation?.assignee || null;
});

// Função para formatar data
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return '';
  }
};

// Função para obter status de disponibilidade do agente da conversa
const getAgentAvailabilityStatus = (agent) => {
  if (!agent) return null;

  // Primeiro tenta obter do agente da conversa
  if (agent.availability_status) {
    return agent.availability_status;
  }

  // Fallback para 'online' se não especificado
  return 'online';
};

// Função para obter nome do status de disponibilidade
const getAvailabilityLabel = (status) => {
  switch (status) {
    case 'online':
      return 'Online';
    case 'offline':
      return 'Offline';
    case 'busy':
      return 'Ocupado';
    default:
      return 'Desconhecido';
  }
};

// Função para abrir modal
const openAgentAssignModal = () => {
  selectedAgentId.value = null;
  showAgentAssignModal.value = true;
};

// Função para atribuir agente
const assignAgent = async () => {
  if (!selectedAgentId.value || agentAssignLoading.value) return;
  agentAssignLoading.value = true;
  try {
    const { data } = await KanbanAPI.assignAgent(
      props.item.id,
      selectedAgentId.value
    );
    if (data) {
      emit('agentAssigned', data);
    }
    showAgentAssignModal.value = false;
    selectedAgentId.value = null;
  } catch (error) {
    console.error('Erro ao atribuir agente:', error);
  } finally {
    agentAssignLoading.value = false;
  }
};

// Função para remover agente
const removeAgent = async (agentId) => {
  try {
    const { data } = await KanbanAPI.removeAgent(props.item.id, agentId);
    if (data) {
      emit('agentRemoved', data);
    }
  } catch (error) {
    console.error('Erro ao remover agente:', error);
  }
};

// Função para obter o nome do agente que atribuiu baseado no ID
const getAssignedByName = (assignedById) => {
  if (!assignedById) return '';

  // Primeiro tenta encontrar na lista de agentes do funnel
  const funnelAgents = props.item?.funnel?.settings?.agents || [];
  const funnelAgent = funnelAgents.find(agent => agent.id === assignedById);

  if (funnelAgent) {
    return funnelAgent.name;
  }

  // Fallback para o agente principal se disponível
  if (props.agentInfo && props.agentInfo.id === assignedById) {
    return props.agentInfo.name;
  }

  // Último fallback: retorna o ID como string
  return assignedById.toString();
};
</script>

<template>
  <div class="agent-info-section">
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users-round-icon lucide-users-round text-slate-600 dark:text-slate-400">
          <path d="M18 21a8 8 0 0 0-16 0"/>
          <circle cx="10" cy="8" r="5"/>
          <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3"/>
        </svg>
        <span class="text-sm text-slate-700 dark:text-slate-300">
          {{ t('KANBAN.ITEM_DETAILS.AGENT_RESPONSIBLE') }}
        </span>
      </div>

      <!-- Botão para adicionar agente -->
      <button
        class="flex items-center justify-center w-8 h-8 bg-slate-100 dark:bg-slate-800 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        @click="openAgentAssignModal"
      >
        <fluent-icon
          icon="person-add"
          size="16"
          class="text-slate-500 dark:text-slate-400"
        />
      </button>
    </div>

    <div class="agent-content">
      <div v-if="loadingAgent" class="loading-text">
        {{ t('KANBAN.ITEM_DETAILS.LOADING_AGENT') }}
      </div>
      <div v-else-if="assignedAgents && assignedAgents.length > 0" class="space-y-3">
        <div class="flex flex-col gap-3">
          <div
            v-for="agent in assignedAgents"
            :key="agent.id"
            class="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
          >
            <!-- Header com avatar e nome -->
            <div class="flex items-center gap-3 mb-2">
              <Avatar
                :name="agent.name"
                :src="agent.avatar_url || agent.thumbnail"
                :size="32"
              />
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {{ agent.name }}
                  </span>
                  <!-- Status de disponibilidade do agente da conversa na mesma linha -->
                  <div
                    v-if="conversationAgent && conversationAgent.id === agent.id"
                    class="relative flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium transition-all duration-300 shadow-sm"
                    :class="{
                      'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800':
                        getAgentAvailabilityStatus(conversationAgent) === 'online',
                      'bg-slate-50 text-slate-600 border border-slate-200 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700':
                        getAgentAvailabilityStatus(conversationAgent) === 'offline',
                      'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800':
                        getAgentAvailabilityStatus(conversationAgent) === 'busy'
                    }"
                  >
                    <!-- Indicador animado para status online -->
                    <div
                      v-if="getAgentAvailabilityStatus(conversationAgent) === 'online'"
                      class="relative flex items-center"
                    >
                      <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <div class="absolute w-2 h-2 bg-green-400 rounded-full animate-ping opacity-75"></div>
                    </div>
                    <!-- Indicador estático para outros status -->
                    <div
                      v-else
                      class="w-2 h-2 rounded-full"
                      :class="{
                        'bg-slate-400': getAgentAvailabilityStatus(conversationAgent) === 'offline',
                        'bg-red-500': getAgentAvailabilityStatus(conversationAgent) === 'busy'
                      }"
                    ></div>

                    <!-- Texto do status -->
                    <span class="leading-none">
                      {{ getAvailabilityLabel(getAgentAvailabilityStatus(conversationAgent)) }}
                    </span>
                  </div>
                  <span
                    v-if="agent.id === agentInfo?.id"
                    class="text-xs bg-woot-100 dark:bg-woot-900 text-woot-600 dark:text-woot-400 px-2 py-0.5 rounded-full"
                  >
                    Principal
                  </span>
                  <!-- Botão de remover agente no lado direito -->
                  <button
                    class="ml-auto p-1 text-slate-400 hover:text-red-500 transition-colors rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
                    @click="removeAgent(agent.id)"
                    title="Remover agente"
                  >
                    <fluent-icon icon="delete" size="16" />
                  </button>
                </div>
                <!-- Informações de atribuição do lado direito -->
                <div class="flex items-center gap-4 mt-1 text-xs text-slate-600 dark:text-slate-400">
                  <!-- Data de atribuição -->
                  <div v-if="agent.assigned_at" class="flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock-icon lucide-clock text-slate-500 dark:text-slate-400">
                      <path d="M12 6v6l4 2"/>
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                    <span class="text-xs">
                      {{ t('KANBAN.AGENT.ASSIGNED_AT') || 'Atribuído em' }}: {{ formatDate(agent.assigned_at) }}
                    </span>
                  </div>

                  <!-- Atribuído por -->
                  <div v-if="agent.assigned_by" class="flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-mouse-pointer-click-icon lucide-mouse-pointer-click text-slate-500 dark:text-slate-400">
                      <path d="M14 4.1 12 6"/>
                      <path d="m5.1 8-2.9-.8"/>
                      <path d="m6 12-1.9 2"/>
                      <path d="M7.2 2.2 8 5.1"/>
                      <path d="M9.037 9.69a.498.498 0 0 1 .653-.653l11 4.5a.5.5 0 0 1-.074.949l-4.349 1.041a1 1 0 0 0-.74.739l-1.04 4.35a.5.5 0 0 1-.95.074z"/>
                    </svg>
                    <span class="text-xs">
                      {{ t('KANBAN.AGENT.ASSIGNED_BY') || 'Atribuído por' }}: {{ getAssignedByName(agent.assigned_by) }}
                    </span>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </div>
      <div v-else-if="agentInfo" class="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
        <Avatar
          :name="agentInfo.name"
          :src="agentInfo.avatar_url"
          :size="32"
        />
        <div class="flex-1">
          <span class="text-sm font-medium text-slate-900 dark:text-slate-100">{{ agentInfo.name }}</span>
        </div>
      </div>
      <div v-else class="no-data-text text-sm text-slate-500">
        {{ t('KANBAN.ITEM_DETAILS.NO_AGENT') }}
      </div>
    </div>

    <!-- Modal de atribuição rápida de agente -->
    <Modal
      v-model:show="showAgentAssignModal"
      :on-close="() => (showAgentAssignModal = false)"
      :show-close-button="true"
      size="medium"
    >
      <div
        class="p-4 bg-white dark:bg-slate-900 rounded-md border border-slate-100 dark:border-slate-800 shadow-lg"
      >
        <h3 class="text-lg font-medium mb-4">Gerenciar Agentes Atribuídos</h3>

        <!-- Seção de agentes já atribuídos -->
        <div v-if="assignedAgents.length > 0" class="mb-4">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Agentes Atribuídos ({{ assignedAgents.length }})
          </h4>
          <div class="space-y-2">
            <div
              v-for="agent in assignedAgents"
              :key="agent.id"
              class="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-md"
            >
              <div class="flex items-center gap-2">
                <Avatar
                  :name="agent.name"
                  :src="agent.avatar_url || agent.thumbnail || ''"
                  :size="24"
                />
                <div>
                  <div
                    class="text-sm font-medium text-slate-900 dark:text-slate-100"
                  >
                    {{ agent.name }}
                  </div>
                  <div class="text-xs text-slate-500 dark:text-slate-400">
                    Atribuído em
                    {{ new Date(agent.assigned_at).toLocaleDateString('pt-BR') }}
                  </div>
                </div>
              </div>
              <button
                class="p-1 text-slate-400 hover:text-red-500 transition-colors"
                @click="removeAgent(agent.id)"
              >
                <fluent-icon icon="delete" size="16" />
              </button>
            </div>
          </div>
        </div>

        <!-- Seção para adicionar novos agentes -->
        <div class="border-t border-slate-200 dark:border-slate-700 pt-4">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Adicionar Novo Agente
          </h4>
          <input
            v-model="agentSearch"
            type="text"
            class="w-full mb-3 px-3 py-2 border border-slate-100 dark:border-slate-800 rounded-md text-xs bg-white dark:bg-slate-800 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            placeholder="Buscar agentes..."
          />
          <div class="space-y-2 mb-4 max-h-40 overflow-y-auto">
            <button
              v-for="agent in filteredAgentList"
              :key="agent.id"
              type="button"
              class="flex items-center w-full px-3 py-1.5 rounded-md transition-colors gap-2 border border-slate-100 dark:border-slate-800 text-xs font-normal hover:bg-slate-100 dark:hover:bg-slate-800"
              :class="[
                selectedAgentId === agent.id
                  ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 font-bold text-woot-600'
                  : '',
                assignedAgents.some(a => a.id === agent.id)
                  ? 'opacity-50 cursor-not-allowed'
                  : '',
              ]"
              :disabled="assignedAgents.some(a => a.id === agent.id)"
              @click="selectedAgentId = agent.id"
            >
              <Avatar
                :name="agent.name || 'Agente'"
                :src="agent.avatar_url || agent.thumbnail || ''"
                :size="20"
                class="mr-2"
              />
              <span class="truncate">{{ agent.name || 'Agente' }}</span>
              <span
                v-if="assignedAgents.some(a => a.id === agent.id)"
                class="ml-auto text-slate-400 text-xs"
                >Já atribuído</span
              >
              <span
                v-else-if="selectedAgentId === agent.id"
                class="ml-auto text-woot-500 text-xs font-semibold"
                >Selecionado</span
              >
            </button>
            <div
              v-if="filteredAgentList.length === 0"
              class="px-3 py-2 text-slate-400 text-xs"
            >
              Nenhum agente encontrado
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="showAgentAssignModal = false"
            >Fechar</Button
          >
          <Button
            variant="solid"
            color="blue"
            size="sm"
            :isLoading="agentAssignLoading"
            :disabled="
              !selectedAgentId ||
              agentAssignLoading ||
              assignedAgents.some(a => a.id === selectedAgentId)
            "
            @click="assignAgent"
            >Adicionar Agente</Button
          >
        </div>
      </div>
    </Modal>
  </div>
</template>

<style scoped>
.agent-info-section {
  background-color: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
}

.dark .agent-info-section {
  background-color: #1e293b;
  border-color: #334155;
}

.agent-content {
  gap: 0.75rem;
  display: flex;
  flex-direction: column;
}

.loading-text,
.no-data-text {
  color: #64748b;
  font-size: 0.875rem;
  padding: 0.5rem 0;
}
</style>
